import {
    Breakpoints,
    Container,
    ContainerPadding,
    CTA,
    Layout,
    LayoutRowGap,
    Text,
    TokenTextAppearance,
    TextTag,
} from '@volkswagen-onehub/components-core'
import {Close} from '@volkswagen-onehub/icons-core'
import {globalColors} from '@volkswagen-onehub/tokens'
import styled from 'styled-components'

import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {
    CloseLayerCTA,
    FORM_BORDER_COLOR,
    LayerHeaderCloseCTA,
    LayerHeaderContainer,
    LayerHeaderTitle,
} from '../../shared/classes'
import {
    RECOVER_COUPON_FORM_ID,
    RecoverCouponForm,
} from '../form/RecoverCouponForm'
import {useSelector} from 'react-redux'
import {RootState} from '../rootReducer'
import {closeRecoverCouponLayer} from './launchers/RecoverCouponRequestLayerHandle'
import {
    eventInfoCommon,
    TrackingAction,
    trackingData,
    trackingEvent,
} from '../../shared/trackingServiceHelper'
import store from '../store'

const RECOVER_COUPON_LAYER_ID = 'recover-coupon-layer'
const RECOVER_COUPON_LAYER_TITLE_ID = 'recover-coupon-layer-title'
const WHITE_BACKGROUND = globalColors.white['000']

const RecoverLayerContainer = styled(Container)`
    > div {
        display: flex;
        flex-direction: column;
        min-height: var(--interaction-layer-height);
    }

    > div > div:nth-child(2) {
        display: flex;
        flex: 1 1 auto;
        width: 100%;
    }

    > div > div:nth-child(2) > * {
        width: 100%;
    }
`

const RecoverLayerHeaderContainer = styled(LayerHeaderContainer)`
    background-color: ${WHITE_BACKGROUND};

    #${RECOVER_COUPON_LAYER_ID} > div > div > div:has(> &) {
        position: sticky;
        top: 0;
        background-color: ${WHITE_BACKGROUND};
        width: 100%;
        z-index: 2;
    }
`

const SubmitContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    box-sizing: border-box;
    width: 100%;
    padding-top: ${(props) => props.theme.size.static250};
    padding-right: ${(props) => props.theme.size.static250};
    padding-bottom: ${(props) => props.theme.size.static250};
    padding-left: ${(props) => props.theme.size.grid001};
    border-top: ${(props) => props.theme.size.border.light} solid
        ${FORM_BORDER_COLOR};
    background-color: ${(props) => props.theme.colors.background.primary};

    @media (min-width: ${Breakpoints.b560}px) {
        padding-left: ${(props) => props.theme.size.static450};
    }

    @media (min-width: ${Breakpoints.b1600}px) {
        padding-left: ${(props) => props.theme.size.static550};
    }

    #${RECOVER_COUPON_LAYER_ID} > div > div > div:has(> &) {
        position: sticky;
        bottom: 0;
        background-color: ${(props) => props.theme.colors.background.primary};
        width: 100%;
        z-index: 1;
    }
`

export function RecoverCouponRequestLayer(): React.JSX.Element {
    const {t} = useTranslation('fa-coupon')

    const thanksRecoverPage = useSelector(
        (state: RootState) => state.userData.thanksRecoverPage
    )
    const configurationPromo = useSelector(
        (state: RootState) => state.configurationPromo.configuration
    )

    const recoveryTitle = configurationPromo.coupon.recovery?.title
        ? configurationPromo.coupon.recovery.title
        : t('layers.recoverCouponRequest.title')
    const recoveryText = configurationPromo.coupon.recovery?.text
        ? configurationPromo.coupon.recovery.text
        : t('layers.recoverCouponRequest.text')
    const recoveryThanksTitle = configurationPromo.coupon.recovery
        ?.thank_you_page?.title
        ? configurationPromo.coupon.recovery.thank_you_page.title
        : t('layers.recoverCouponRequest.thanks.title')
    const recoveryThanksText = configurationPromo.coupon.recovery
        ?.thank_you_page?.text
        ? configurationPromo.coupon.recovery.thank_you_page.text
        : t('layers.recoverCouponRequest.thanks.text')
    const recoveryCTA = configurationPromo.coupon.recovery?.cta
        ? configurationPromo.coupon.recovery.cta
        : t('recover.submit')
    const layerTitle = thanksRecoverPage ? recoveryThanksTitle : recoveryTitle

    useEffect(() => {
        trackingEvent(TrackingAction.VW_BASIC_FORM_LAYER_LOAD, {
            eventInfo: {
                ...eventInfoCommon(),
                contentId: recoveryTitle,
            },
            form: {
                FormName:
                    store.getState().configurationPromo.configuration
                        .tracking_application_id +
                    ' - ' +
                    recoveryTitle,
                FormStart: true,
                FormType: trackingData.FORM_TYPE,
                CycleID: store.getState().userData.formCycleID,
            },
            process: {
                Name: recoveryTitle,
                StepName: recoveryTitle,
            },
        })
    }, [])

    return (
        <div id={RECOVER_COUPON_LAYER_ID}>
            <RecoverLayerContainer wrap="always">
                <RecoverLayerHeaderContainer>
                    {layerTitle ? (
                        <LayerHeaderTitle
                            id={RECOVER_COUPON_LAYER_TITLE_ID}
                            tag={TextTag.h2}
                            bold
                            appearance={TokenTextAppearance.headline300}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: layerTitle,
                                }}
                            />
                        </LayerHeaderTitle>
                    ) : undefined}
                    <LayerHeaderCloseCTA
                        tag="button"
                        emphasis="tertiary"
                        aria-label={t('cta.closeLayerLabel')}
                        iconPosition="top"
                        onClick={() => {
                            closeRecoverCouponLayer()
                            trackingEvent(
                                TrackingAction.VW_BASIC_FORM_LAYER_CLOSE_CLICK,
                                {
                                    eventInfo: {
                                        ...eventInfoCommon(),
                                        contentId: recoveryTitle,
                                    },
                                    form: {
                                        FormName:
                                            store.getState().configurationPromo
                                                .configuration
                                                .tracking_application_id +
                                            ' - ' +
                                            recoveryTitle,
                                        FormStart: false,
                                        FormType: trackingData.FORM_TYPE,
                                    },
                                }
                            )
                        }}
                        icon={<Close aria-hidden />}
                    />
                </RecoverLayerHeaderContainer>

                <Container
                    padding={{
                        left: {
                            [Breakpoints.default]: ContainerPadding.grid001,
                            [Breakpoints.b560]: ContainerPadding.static450,
                            [Breakpoints.b1600]: ContainerPadding.static550,
                        },
                        right: {
                            [Breakpoints.default]: ContainerPadding.grid001,
                            [Breakpoints.b560]: ContainerPadding.static450,
                            [Breakpoints.b1600]: ContainerPadding.static550,
                        },
                        top: ContainerPadding.static350,
                        bottom: ContainerPadding.static350,
                    }}
                >
                    {!thanksRecoverPage ? (
                        <Layout
                            rowGap={LayoutRowGap.static500}
                            allowOverflowingContent
                        >
                            <Text
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.copy200}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: recoveryText,
                                    }}
                                />
                            </Text>
                            <Text
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.copy100}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'layers.couponRequest.required'
                                        ),
                                    }}
                                />
                            </Text>

                            <RecoverCouponForm />
                        </Layout>
                    ) : (
                        <>
                            <Text
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.copy200}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: recoveryThanksText,
                                    }}
                                />
                            </Text>

                            <CloseLayerCTA
                                tag="button"
                                emphasis="secondary"
                                onClick={() => closeRecoverCouponLayer()}
                                title={t(
                                    'layers.couponRequest.thanks.ctaLabel'
                                )}
                                aria-label={t(
                                    'layers.couponRequest.thanks.ctaLabel'
                                )}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'layers.couponRequest.thanks.cta'
                                        ),
                                    }}
                                />
                            </CloseLayerCTA>
                        </>
                    )}
                </Container>

                {!thanksRecoverPage ? (
                    <SubmitContainer>
                        <CTA
                            tag="button"
                            emphasis="primary"
                            type="submit"
                            {...({form: RECOVER_COUPON_FORM_ID} as {
                                form: string
                            })}
                            title={recoveryCTA}
                            aria-label={recoveryCTA}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: recoveryCTA,
                                }}
                            />
                        </CTA>
                    </SubmitContainer>
                ) : undefined}
            </RecoverLayerContainer>
        </div>
    )
}
